import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { logoutUser } from "../services/authService";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { isPushSupported, subscribeToPush, unsubscribeFromPush, isPushSubscribed } from "../services/pushService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMusic,
  faUser,
  faPeopleGroup,
  faHome,
  faPlus,
  faUsers,
  faDoorOpen,
  faBell,
  faBellSlash
} from "@fortawesome/free-solid-svg-icons";
import UserAvatar from "./UserAvatar";
import "./NavBar.css";

function NavBar() {
    const { user, logout, loading } = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isCommunityDropdownOpen, setIsCommunityDropdownOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const notificationRef = useRef(null);
    const [pushEnabled, setPushEnabled] = useState(false);
    const [pushSupported] = useState(isPushSupported());

    useEffect(() => {
        // Fetch unread notifications count when user is authenticated
        if (user) {
            fetchUnreadCount();
            // Poll for new notifications every 60 seconds
            const intervalId = setInterval(fetchUnreadCount, 60000);
            // Check push subscription status
            if (pushSupported) {
                isPushSubscribed().then(setPushEnabled).catch(() => {});
            }
            return () => clearInterval(intervalId);
        }
    }, [user, pushSupported]);

    useEffect(() => {
        // Handle clicks outside the notification dropdown
        function handleClickOutside(event) {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setIsNotificationsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchUnreadCount = async () => {
        try {
            // Import the function dynamically to avoid the import error
            const { getUnreadNotificationCount } = await import("../services/notificationService");
            const response = await getUnreadNotificationCount();
            setUnreadCount(response.unread_count);
        } catch (error) {
        }
    };
    
    // Function to mark all notifications as read
    const handleMarkAllAsRead = async () => {
        try {
            // Import the markAllAsRead function dynamically
            const { markAllNotificationsAsRead } = await import("../services/notificationService");
            await markAllNotificationsAsRead();
            
            // Update the UI immediately
            setUnreadCount(0);
            
            // Refresh the notifications list
            const { fetchNotifications } = await import("../services/notificationService");
            const response = await fetchNotifications({ limit: 5 });
            setNotifications(response.notifications);
        } catch (error) {
        }
    };

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const toggleCommunityDropdown = (e) => {
        e.preventDefault();
        setIsCommunityDropdownOpen(!isCommunityDropdownOpen);
    };

    const toggleNotifications = async (e) => {
        e.preventDefault();
        setIsNotificationsOpen(!isNotificationsOpen);
        
        // If opening the notifications, fetch recent ones
        if (!isNotificationsOpen) {
            try {
                const { fetchNotifications } = await import("../services/notificationService");
                const response = await fetchNotifications({ limit: 5 });
                setNotifications(response.notifications);
            } catch (error) {
            }
        }
    };

    const handleLogout = async () => {
        try {
            // Immediately update UI by calling logout first
            logout();
            
            // Close menu if open
            if (isMenuOpen) {
                setIsMenuOpen(false);
            }
            
            // Then handle the server-side logout
            try {
                await logoutUser();
            } catch (error) {
                // Continue with client-side logout even if server fails
            }
            
            // Navigate after a slight delay to ensure state updates propagate
            setTimeout(() => {
                navigate("/login", { replace: true });
            }, 50);
        } catch (error) {
            navigate("/login", { replace: true });
        }
    };

    const handleTogglePush = async () => {
        try {
            if (pushEnabled) {
                await unsubscribeFromPush();
                setPushEnabled(false);
            } else {
                const success = await subscribeToPush();
                setPushEnabled(success);
            }
        } catch (error) {
            // Permission denied or other error
        }
    };

    // Format notification timestamp relative to now
    const formatTimeAgo = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHour = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHour / 24);

        if (diffSec < 60) return 'just now';
        if (diffMin < 60) return `${diffMin}m ago`;
        if (diffHour < 24) return `${diffHour}h ago`;
        if (diffDay < 7) return `${diffDay}d ago`;
        return date.toLocaleDateString();
    };

    // NavBar is now explicitly desktop-only with d-none d-lg-block class
    return (
        <nav className="navbar navbar-expand-lg navbar-dark bg-dark sticky-top d-none d-lg-block">
            <div className="container-fluid navbar-container">
                <Link className="navbar-brand" to="/">
                    <img src="/flavicon_dark.ico" alt="MUSI logo" className="navbar-logo" />
                    MUSI
                </Link>
                
                {/* Menu toggle button */}
                <button
                    className="navbar-toggler custom-toggler"
                    type="button"
                    onClick={toggleMenu}
                    aria-controls="navbarNav"
                    aria-expanded={isMenuOpen}
                    aria-label="Toggle navigation"
                >
                    <div className="custom-toggler-icon">
                        <div className={`toggler-line ${isMenuOpen ? 'open' : ''}`}></div>
                        <div className={`toggler-line ${isMenuOpen ? 'open' : ''}`}></div>
                        <div className={`toggler-line ${isMenuOpen ? 'open' : ''}`}></div>
                    </div>
                </button>
                
                <div 
                    className={`collapse navbar-collapse ${isMenuOpen ? 'show' : ''}`} 
                    id="navbarNav"
                >
                    <ul className="navbar-nav ms-auto">
                        {loading ? (
                            <li className="nav-item">
                                <span className="nav-link text-muted">
                                    <small>{t('navigation.loading')}</small>
                                </span>
                            </li>
                        ) : user ? (
                            <>
                                <li className="nav-item">
                                    <Link className="nav-link" to="/" onClick={() => setIsMenuOpen(false)}>
                                        <FontAwesomeIcon icon={faHome} className="me-1" size="xs" />
                                        {t('navigation.events')}
                                    </Link>
                                </li>

                                <li className="nav-item">
                                    <Link className="nav-link" to="/songs" onClick={() => setIsMenuOpen(false)}>
                                        <FontAwesomeIcon icon={faMusic} className="me-1" size="xs" />
                                        {t('navigation.songbook')}
                                    </Link>
                                </li>
                                
                                {/* Communities Dropdown */}
                                {user && (
                                  <li className="nav-item dropdown">
                                <button
                                className="nav-link dropdown-toggle"
                                onClick={toggleCommunityDropdown}
                                aria-expanded={isCommunityDropdownOpen}
                                style={{ background: 'none', border: 'none', textDecoration: 'none', cursor: 'pointer' }}
                                >
                                <FontAwesomeIcon icon={faPeopleGroup} className="me-1" size="xs" />
                                {t('navigation.communities')}
                                </button>
                                    <ul className={`dropdown-menu dropdown-menu-dark ${isCommunityDropdownOpen ? 'show' : ''}`}>
                                      <li>
                                        <Link
                                          className="dropdown-item"
                                          to="/community/create"
                                          onClick={() => setIsCommunityDropdownOpen(false)}
                                        >
                                          <FontAwesomeIcon icon={faPlus} className="me-2" size="xs" />
                                          {t('navigation.createNewCommunity')}
                                        </Link>
                                      </li>
                                      <li>
                                        <Link
                                          className="dropdown-item"
                                          to="/community/join"
                                          onClick={() => setIsCommunityDropdownOpen(false)}
                                        >
                                          <FontAwesomeIcon icon={faDoorOpen} className="me-2" size="xs" />
                                          {t('navigation.joinCommunity')}
                                        </Link>
                                      </li>
                                      <li>
                                        <Link
                                          className="dropdown-item"
                                          to="/community/list"
                                          onClick={() => setIsCommunityDropdownOpen(false)}
                                        >
                                          <FontAwesomeIcon icon={faUsers} className="me-2" size="xs" />
                                          {t('navigation.allCommunities')}
                                        </Link>
                                      </li>
                                    </ul>
                                  </li>
                                )}

                                {/* Notification Bell with text */}
                                <li className="nav-item dropdown" ref={notificationRef}>
                                <button
                                className={`nav-link ${isNotificationsOpen ? 'show' : ''}`}
                                onClick={toggleNotifications}
                                aria-expanded={isNotificationsOpen}
                                style={{ background: 'none', border: 'none', textDecoration: 'none', cursor: 'pointer' }}
                                >
                                <span className="notification-icon-wrapper">
                                    <FontAwesomeIcon icon={faBell} className="me-1" size="xs" />
                                    {unreadCount > 0 && (
                                    <span className="notification-dot"></span>
                                    )}
                                </span>
                                <span>{t('navigation.notifications')}</span>
                                </button>
                                    <div className={`dropdown-menu dropdown-menu-dark notification-dropdown ${isNotificationsOpen ? 'show' : ''}`}>
                                        <div className="notification-header">
                                            <h6 className="dropdown-header">{t('navigation.notifications')}</h6>
                                            <div className="d-flex align-items-center gap-2">
                                                {pushSupported && (
                                                    <button
                                                        className={`btn btn-sm ${pushEnabled ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                                                        onClick={handleTogglePush}
                                                        title={pushEnabled ? t('navigation.pushDisable') : t('navigation.pushEnable')}
                                                        style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                                    >
                                                        <FontAwesomeIcon icon={pushEnabled ? faBell : faBellSlash} size="sm" className="me-1" />
                                                        {pushEnabled ? t('navigation.pushOn') : t('navigation.pushOff')}
                                                    </button>
                                                )}
                                                {unreadCount > 0 && (
                                                    <button
                                                        className="dropdown-item text-primary"
                                                        onClick={handleMarkAllAsRead}
                                                        style={{ background: 'none', border: 'none', textDecoration: 'none', cursor: 'pointer', textAlign: 'left', width: 'auto' }}
                                                    >
                                                        <small>{t('navigation.markAllAsRead')}</small>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="notification-list">
                                            {notifications.length === 0 ? (
                                                <span className="dropdown-item text-muted">{t('navigation.noNotifications')}</span>
                                            ) : (
                                                notifications.map(notification => (
                                                    <Link
                                                        key={notification.id}
                                                        to={notification.reference_type === 'event' ? `/dashboard?event=${notification.reference_id}` :
                                                            notification.reference_type === 'community' ? `/community/${notification.reference_id}` :
                                                            '/notifications'}
                                                        className={`dropdown-item notification-item ${notification.is_read ? '' : 'unread'}`}
                                                        onClick={() => setIsNotificationsOpen(false)}
                                                    >
                                                        <div className="d-flex align-items-start">
                                                            <div className="notification-avatar me-2">
                                                                {notification.actor ? (
                                                                    <UserAvatar
                                                                        profileImage={notification.actor.profile_image}
                                                                        displayName={notification.actor.display_name}
                                                                        email={notification.actor.email}
                                                                        size={32}
                                                                        avatarOnly={true}
                                                                    />
                                                                ) : (
                                                                    <div
                                                                        className="rounded-circle d-flex justify-content-center align-items-center"
                                                                        style={{
                                                                            width: 32,
                                                                            height: 32,
                                                                            backgroundColor: '#6c757d',
                                                                            color: 'white'
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faBell} size="sm" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="notification-text-content">
                                                                <div className="notification-content text-wrap">
                                                                    {notification.content.length > 50 ?
                                                                        `${notification.content.substring(0, 50)}...` :
                                                                        notification.content
                                                                    }
                                                                </div>
                                                                <div className="notification-meta">
                                                                    <small className="text-muted" style={{ color: '#ffffff !important' }}>{formatTimeAgo(notification.created_at)}</small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Link>
                                                ))
                                            )}
                                        </div>
                                        <div className="dropdown-divider"></div>
                                        <Link
                                            to="/notifications"
                                            className="dropdown-item text-center"
                                            onClick={() => setIsNotificationsOpen(false)}
                                        >
                                            {t('navigation.allNotifications')}
                                        </Link>
                                    </div>
                                </li>

                                <li className="nav-item">
                                    <Link className="nav-link" to="/profile" onClick={() => setIsMenuOpen(false)}>
                                        <FontAwesomeIcon icon={faUser} className="me-1" size="xs" />
                                        {t('navigation.myProfile')}
                                    </Link>
                                </li>

                                <li className="nav-item">
                                    <button
                                        className="nav-link text-danger border-0 bg-transparent"
                                        onClick={() => {
                                            setIsMenuOpen(false);
                                            handleLogout();
                                        }}
                                    >
                                        {t('navigation.logout')}
                                    </button>
                                </li>
                            </>
                        ) : (
                            <>
                                <li className="nav-item">
                                    <Link className="nav-link" to="/login" onClick={() => setIsMenuOpen(false)}>
                                        {t('navigation.login')}
                                    </Link>
                                </li>

                                <li className="nav-item">
                                    <Link className="nav-link" to="/register" onClick={() => setIsMenuOpen(false)}>
                                        {t('navigation.register')}
                                    </Link>
                                </li>
                            </>
                        )}
                    </ul>
                </div>
            </div>
        </nav>
    );
}

export default NavBar;